import { useEffect, useRef, useState } from 'react';
import { apiFetch } from '../../fetchInterceptor';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Card } from '../ui/card';
import { ScrollArea } from '../ui/scroll-area';
import { Sparkles, Send, Loader2, X, Bot, User as UserIcon } from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Per-doc-type suggested starter questions — surface the most useful
// prompts so the user doesn't have to think of one. The chip click just
// pre-fills the input; the user still has to press send.
const STARTERS = {
  tax_return: [
    'Find me deductions I missed.',
    'What can I do to lower next year\'s tax bill?',
    'Are there any obvious errors on this return?',
  ],
  contract: [
    'Summarize the key obligations.',
    'When can either party terminate?',
    'Extract the renewal terms and important dates.',
  ],
  bill: [
    'When is this due and who is it from?',
    'Suggest the right GL account for this expense.',
    'Is this likely a duplicate?',
  ],
  invoice: [
    'When is payment due and what\'s the total?',
    'Are there any tax or shipping line items?',
  ],
  receipt: [
    'Categorize this expense.',
    'Is this a deductible business expense?',
  ],
  bank_statement: [
    'Spot any unusual or large charges.',
    'What were the biggest deposits this month?',
  ],
  w9_form: [
    'Should I issue a 1099 to this vendor?',
    'Extract the TIN and entity classification.',
  ],
  unknown: [
    'What kind of document is this?',
    'Summarize the most important details.',
  ],
};

// Common 1040 lines surfaced in the inline-correction dropdown. Keeping
// this list curated rather than dumping every possible key (>50) so the
// UI stays scannable. Power users can still hit the OCR review screen
// to add anything missing.
const TAX_LINE_OPTIONS_1040 = [
  { key: '1040_line_1a_wages',                    label: 'Line 1a — Wages, salaries, tips' },
  { key: '1040_line_1z_total_wages',              label: 'Line 1z — Total wages' },
  { key: '1040_line_2b_taxable_interest',         label: 'Line 2b — Taxable interest' },
  { key: '1040_line_3b_ordinary_dividends',       label: 'Line 3b — Ordinary dividends' },
  { key: '1040_line_4b_ira_taxable',              label: 'Line 4b — IRA distributions taxable' },
  { key: '1040_line_5b_pensions_taxable',         label: 'Line 5b — Pensions taxable' },
  { key: '1040_line_6b_social_security_taxable',  label: 'Line 6b — Social Security taxable' },
  { key: '1040_line_7_capital_gain_loss',         label: 'Line 7 — Capital gain/loss' },
  { key: '1040_line_9_total_income',              label: 'Line 9 — Total income' },
  { key: '1040_line_11_agi',                      label: 'Line 11 — Adjusted gross income' },
  { key: '1040_line_12_standard_or_itemized',     label: 'Line 12 — Standard/Itemized deduction' },
  { key: '1040_line_13_qbi_deduction',            label: 'Line 13 — QBI deduction' },
  { key: '1040_line_15_taxable_income',           label: 'Line 15 — Taxable income' },
  { key: '1040_line_16_tax',                      label: 'Line 16 — Tax' },
  { key: '1040_line_24_total_tax',                label: 'Line 24 — Total tax' },
  { key: '1040_line_25a_withholding_w2',          label: 'Line 25a — W-2 withholding' },
  { key: '1040_line_33_total_payments',           label: 'Line 33 — Total payments' },
  { key: '1040_line_35a_refund',                  label: 'Line 35a — Refund' },
  { key: '1040_line_37_amount_owed',              label: 'Line 37 — Amount owed' },
];

const TAX_LINE_OPTIONS_W2 = [
  { key: 'w2_box_1_wages',              label: 'Box 1 — Wages' },
  { key: 'w2_box_2_federal_withholding', label: 'Box 2 — Federal withholding' },
  { key: 'w2_box_3_ss_wages',           label: 'Box 3 — SS wages' },
  { key: 'w2_box_4_ss_tax_withheld',    label: 'Box 4 — SS tax withheld' },
  { key: 'w2_box_5_medicare_wages',     label: 'Box 5 — Medicare wages' },
  { key: 'w2_box_6_medicare_tax',       label: 'Box 6 — Medicare tax' },
];

// Guess the most likely line_key from the user's question — keeps the
// dropdown pre-selected to the right line so the user just types a
// value and clicks save.
function guessLineKey(question, docType) {
  const q = (question || '').toLowerCase();
  const options = docType === 'w2_form' ? TAX_LINE_OPTIONS_W2 : TAX_LINE_OPTIONS_1040;
  // Look for explicit line/box references first.
  const lineMatch = q.match(/line\s*(\d+[a-z]?)/);
  if (lineMatch) {
    const num = lineMatch[1];
    const hit = options.find((o) => o.key.includes(`_line_${num}_`) || o.key.includes(`_line_${num}z_`));
    if (hit) return hit.key;
  }
  const boxMatch = q.match(/box\s*(\d+)/);
  if (boxMatch) {
    const hit = options.find((o) => o.key.includes(`_box_${boxMatch[1]}_`));
    if (hit) return hit.key;
  }
  // Fall back to keyword matches.
  const keywordHits = options.filter((o) => {
    const tokens = o.label.toLowerCase().split(/[^a-z0-9]+/).filter((t) => t.length > 3);
    return tokens.some((t) => q.includes(t));
  });
  if (keywordHits.length === 1) return keywordHits[0].key;
  return options[0]?.key;
}

/**
 * Phase 3 — Inline Ask AI panel for a single OCR job. Renders the
 * conversation history, doc-aware starter chips, and a textarea that
 * POSTs to /api/ocr/{jobId}/ask. Closeable via the X button.
 */
export default function InlineAskAI({ jobId, docType, onClose }) {
  const [turns, setTurns] = useState([]);
  const [threadId, setThreadId] = useState(null);
  const [question, setQuestion] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await apiFetch(`${API_URL}/api/ocr/${jobId}/thread`);
        if (!r.ok) return;
        const d = await r.json();
        if (cancelled) return;
        setThreadId(d.thread_id || null);
        setTurns(d.turns || []);
      } catch { /* fine — no thread yet */ }
    })();
    return () => { cancelled = true; };
  }, [jobId]);

  useEffect(() => {
    // Scroll to bottom on new turn.
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [turns, sending]);

  const send = async (text) => {
    const q = (text ?? question).trim();
    if (!q || sending) return;
    setSending(true);
    setQuestion('');
    // Optimistic user bubble.
    setTurns(prev => [...prev, { question: q, answer: null, optimistic: true }]);
    try {
      const r = await apiFetch(`${API_URL}/api/ocr/${jobId}/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: q, thread_id: threadId }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Ask AI failed.');
      }
      const d = await r.json();
      setThreadId(d.thread_id);
      setTurns(prev => {
        const next = prev.filter(t => !t.optimistic);
        next.push({
          question: q,
          answer: d.answer,
          asked_at: d.asked_at,
          model: d.model,
          // Pass through the hallucination flag so the turn renderer
          // can offer a "Set the value yourself" patch UI when the
          // model couldn't find the data in extraction.
          hallucination_scrubbed: !!d.hallucination_scrubbed,
          scrubbed_amounts: d.scrubbed_amounts || [],
          // The model produced a soft refusal (no data to cite). Same
          // UI affordance as the hallucination scrub.
          is_refusal: !!d.is_refusal,
        });
        return next;
      });
    } catch (e) {
      setTurns(prev => prev.filter(t => !t.optimistic));
      toast.error(e.message || 'AI is taking a breather.');
    } finally {
      setSending(false);
    }
  };

  // Save a user-supplied tax-line value. After save we automatically
  // re-ask the question that triggered the refusal so the user
  // immediately sees the corrected answer — turns a 3-step rabbit hole
  // (refuse → patch → re-ask) into a 1-click loop.
  const saveTaxLine = async (turnIdx, lineKey, value) => {
    const q = turns[turnIdx]?.question;
    if (!lineKey || !Number.isFinite(value) || !q) return;
    try {
      const r = await apiFetch(`${API_URL}/api/ocr/${jobId}/tax-line-correction`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line_key: lineKey, value }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Could not save the value.');
      }
      toast.success(`Saved ${lineKey} = $${value.toLocaleString()}`);
      // Remove the refused turn and re-ask the original question.
      setTurns((prev) => prev.filter((_, i) => i !== turnIdx));
      send(q);
    } catch (e) {
      toast.error(e.message || 'Save failed.');
    }
  };

  const starters = STARTERS[docType] || STARTERS.unknown;

  return (
    <Card
      className="border-2 border-violet-200 dark:border-violet-900 bg-gradient-to-br from-white to-violet-50/30 dark:from-slate-950 dark:to-violet-950/20"
      data-testid="inline-ask-ai-panel"
    >
      <div className="flex items-center justify-between p-3 border-b border-violet-100 dark:border-violet-900">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-md bg-violet-100 dark:bg-violet-900">
            <Sparkles className="w-4 h-4 text-violet-600 dark:text-violet-300" />
          </div>
          <div>
            <div className="text-sm font-semibold">Ask AI Buck about this document</div>
            <div className="text-xs text-muted-foreground">
              Powered by {docType === 'tax_return' || docType === 'contract' ? 'Claude Sonnet 4.5' : 'Gemini 2.5 Pro'}
            </div>
          </div>
        </div>
        {onClose && (
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="inline-ask-ai-close">
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>

      <div ref={scrollRef} className="max-h-80 overflow-y-auto p-3 space-y-3">
        {turns.length === 0 && !sending && (
          <div className="text-xs text-muted-foreground italic">
            No questions yet. Try a starter below — or type your own.
          </div>
        )}
        {turns.map((t, i) => (
          <div key={i} className="space-y-2" data-testid={`ask-turn-${i}`}>
            <div className="flex items-start gap-2">
              <UserIcon className="w-4 h-4 mt-0.5 text-muted-foreground" />
              <div className="text-sm font-medium flex-1">{t.question}</div>
            </div>
            {t.answer && (
              <div className="flex items-start gap-2 pl-2">
                <Bot className="w-4 h-4 mt-0.5 text-violet-600" />
                <div className="text-sm whitespace-pre-wrap leading-relaxed flex-1">{t.answer}</div>
              </div>
            )}
            {/* Inline correction widget — surfaces when AI Buck refused
                because the requested value wasn't in the extraction. Only
                renders for tax_return / w2_form documents (the only places
                we have a well-defined line-key catalog). The user picks a
                line, types the amount they can see on the PDF, hits Save,
                and we immediately re-ask the original question. */}
            {t.answer && (t.hallucination_scrubbed || t.is_refusal) && ['tax_return', 'w2_form'].includes(docType) && (
              <TaxLineCorrectionInline
                docType={docType}
                question={t.question}
                onSave={(lineKey, value) => saveTaxLine(i, lineKey, value)}
                disabled={sending}
              />
            )}
            {t.optimistic && (
              <div className="flex items-start gap-2 pl-2">
                <Loader2 className="w-4 h-4 mt-0.5 text-violet-600 animate-spin" />
                <div className="text-sm text-muted-foreground">AI Buck is thinking…</div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="p-3 border-t border-violet-100 dark:border-violet-900 space-y-2">
        {turns.length === 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="ask-ai-starters">
            {starters.map((s, i) => (
              <button
                key={i}
                onClick={() => send(s)}
                disabled={sending}
                className={cn(
                  'text-xs px-2.5 py-1 rounded-full border bg-white hover:bg-violet-50 dark:bg-slate-900 dark:hover:bg-violet-950',
                  'border-violet-200 dark:border-violet-800 text-violet-900 dark:text-violet-200 transition-colors',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                )}
                data-testid={`ask-ai-starter-${i}`}
              >
                {s}
              </button>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <Textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Ask anything about this document…"
            rows={2}
            disabled={sending}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            className="resize-none text-sm"
            data-testid="ask-ai-input"
          />
          <Button
            onClick={() => send()}
            disabled={sending || !question.trim()}
            className="self-end bg-violet-600 hover:bg-violet-700"
            data-testid="ask-ai-send"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </Card>
  );
}


// ─── Inline tax-line correction widget ────────────────────────────────
// Used inside InlineAskAI when AI Buck refuses because the requested
// value isn't in the extraction. The user picks the line they can see
// on the PDF, types the value, and the parent re-asks the original
// question against the patched context.
function TaxLineCorrectionInline({ docType, question, onSave, disabled }) {
  const options = docType === 'w2_form' ? TAX_LINE_OPTIONS_W2 : TAX_LINE_OPTIONS_1040;
  const [lineKey, setLineKey] = useState(() => guessLineKey(question, docType));
  const [valueStr, setValueStr] = useState('');
  const value = parseFloat(valueStr.replace(/[$,\s]/g, ''));
  const canSave = lineKey && Number.isFinite(value) && !disabled;

  return (
    <div
      className="ml-6 mt-1 rounded-md border border-amber-200 dark:border-amber-900 bg-amber-50/60 dark:bg-amber-950/20 p-2.5 space-y-2"
      data-testid="tax-line-correction-inline"
    >
      <div className="text-[11px] font-medium text-amber-900 dark:text-amber-200 flex items-center gap-1.5">
        <Sparkles className="w-3 h-3" />
        Can you see this on the PDF? Tell AI Buck so it remembers.
      </div>
      <div className="flex gap-1.5 items-center">
        <select
          value={lineKey}
          onChange={(e) => setLineKey(e.target.value)}
          disabled={disabled}
          className="text-xs px-2 py-1.5 rounded-md border bg-background flex-1 min-w-0"
          data-testid="tax-line-correction-key"
        >
          {options.map((o) => (
            <option key={o.key} value={o.key}>{o.label}</option>
          ))}
        </select>
        <input
          type="text"
          inputMode="decimal"
          value={valueStr}
          onChange={(e) => setValueStr(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canSave) {
              e.preventDefault();
              onSave(lineKey, value);
            }
          }}
          placeholder="$0.00"
          disabled={disabled}
          className="text-xs px-2 py-1.5 rounded-md border bg-background w-24 font-mono text-right"
          data-testid="tax-line-correction-value"
        />
        <Button
          type="button"
          size="sm"
          disabled={!canSave}
          onClick={() => onSave(lineKey, value)}
          className="h-7 px-2.5 text-xs bg-amber-600 hover:bg-amber-700 text-white"
          data-testid="tax-line-correction-save"
        >
          Save &amp; re-ask
        </Button>
      </div>
    </div>
  );
}
